import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { useModal } from "react-modal-hook";
import { TemplateClassNames } from "../../../../types/template-layouts";
import { PageModule, SessionPreview } from "../../../../types/working-model";
import { useScreenMediaQuery } from "../../../../utils/use-screen-media-query";
import TranslateLangs from "../../../../utils/translate-langs.json";
import ModalComponent from "../../../general-ui/modal/modal";
import Search from "../../../general-ui/search-bar/search";
import { SelectOption } from "../../../general-ui/select/select";
import { EIcon } from '../../../general-ui/icon/icon';
import SelectNative, {
	TSelectViewType,
} from "../../../general-ui/select/select-native";
import "./session-filter.scss";
import { useTranslation } from "react-i18next";

export interface SessionFilterArgs {
	language?: string;
	search?: string;
	category?: string;
	day?: number;
}
interface SessionFilterProps {
	module: PageModule;
	template: TemplateClassNames | undefined;
	sessions: SessionPreview[];
	isLive: boolean;
	onFilterChange?: (props: SessionFilterArgs) => void;
}
export const SessionFilter = ({
	sessions,
	onFilterChange,
	isLive,
	template,
}: SessionFilterProps): JSX.Element => {
	const [selectedLanguage, setSelectedLanguage] = useState<string>("");
	const [selectedDate, setSelectedDate] = useState<number>(0);
	const [selectedCategory, setSelectedCategory] = useState<string>("");
	const [, setSearch] = useState<string>();
	const { t } = useTranslation("homepage");
	const { isLessThan768 } = useScreenMediaQuery();

	const daysOptions = useMemo<SelectOption[]>(() => {
		const existingDays: (string | number)[] = [];
		const result = sessions
			.map((s) => {
				const date = new Date(s.timestamp || "");
				if (isNaN(date.getTime())) {
					return null;
				}
				return {
					label: `${date.toLocaleString("default", {
						month: "long",
					})} ${date.getDate()}`,
					value: s.timestamp || 0,
				} as SelectOption;
			})
			.filter((f) => {
				if (!f) return false;
				// dont include duplicate days
				// use labels as timestamps are different for same day events with differing times
				const alreadyExists = existingDays.includes(f.label);
				existingDays.push(f.label);
				return !alreadyExists;
			}) as SelectOption[];
		return [{ label: t("All days"), value: "" }, ...result];
	}, [sessions, t]);

	const languageOptions = useMemo<SelectOption[]>(() => {
		const result = sessions
			.map((s) => s.languages)
			.flat()
			.filter((x, i, a) => a.indexOf(x) === i)
			.map((s) => {
				const fullLanguage = TranslateLangs.find(f => f.code === s);
				return {
					label: fullLanguage?.label, value: s
				};
			});
		return [{ label: t("All languages"), value: "" }, ...result];
	}, [sessions, t]);

	const categoryOptions = useMemo<SelectOption[]>(() => {
		const result = sessions
			.map((s) => s.tracks)
			.flat()
			.filter((x, i, a) => a.indexOf(x) === i)
			.map(
				(s) =>
				({
					label: s,
					value: s,
				} as SelectOption)
			);
		return [{ label: t("All tracks"), value: "" }, ...result];
	}, [sessions, t]);

	const renderInlineDays = () => {
		const data = daysOptions.map((day) => {
			//Typescript doesnt like string interpolation in the translation funciton, overrideing with any
			let label = day.label;
			if (day.value) {
				const split = day.label.split(" "); //splits month and numbered day up e.g. (August 2)
				const month = t(`Month.${split[0]}` as any) ?? split[0];
				label = month + " " + split[1];
			}
			return (
				<button
					key={day.value}
					onClick={() => {
						setSelectedDate(day.value as number);
						onFilterChange &&
							onFilterChange({
								category: selectedCategory,
								day: day.value as number,
								language: selectedLanguage,
							});
					}}
					className={classNames({
						"session-selected-day ": day.value === selectedDate,
						"session-day-button ": true,
					})}
				>
					{label}
				</button>
			);
		});
		return (
			<div className="agenda-inline-days">
				{data}
			</div>
		);
	};

	const renderDaysFilter = (selectViewType: TSelectViewType): JSX.Element => {
		return (
			<>
				{/* Test to any value for test inline view */}
				{daysOptions.length < 4 && !isLessThan768 ? (
					renderInlineDays()
				) : (
					<SelectNative
						value={selectedDate}
						viewType={selectViewType}
						options={daysOptions}
						template={template}
						label={isLessThan768 ? 'Days' : ''}
						onChange={(e) => {
							const value = e.target.value;
							const date = parseInt(value, 10);
							setSelectedDate(date);

							onFilterChange &&
								onFilterChange({
									category: selectedCategory,
									day: date,
									language: selectedLanguage,
								});
						}}
					/>
				)}
			</>
		);
	};

	const renderLanguageFilter = (selectViewType: TSelectViewType): JSX.Element => {
		return (
			<SelectNative
				value={selectedLanguage}
				viewType={selectViewType}
				template={template}
				options={languageOptions}
				label={isLessThan768 ? 'Languages' : ''}
				onChange={(e) => {
					const value = e.target.value;
					setSelectedLanguage(value);

					onFilterChange &&
						onFilterChange({
							category: selectedCategory,
							day: selectedDate,
							language: value,
						});
				}}
			/>
		);
	};

	const renderCategoryFilter = (selectViewType: TSelectViewType): JSX.Element => {
		return (
			<SelectNative
				value={selectedCategory}
				template={template}
				options={categoryOptions}
				viewType={selectViewType}
				label={isLessThan768 ? 'Tracks' : ''}
				onChange={(e) => {
					const value = e.target.value;
					setSelectedCategory(value);

					onFilterChange &&
						onFilterChange({
							category: value,
							day: selectedDate,
							language: selectedLanguage,
						});
				}}
			/>
		);
	};

	const handleSearchChange = useCallback(
		(text) => {
			setSearch(text);
			onFilterChange &&
				onFilterChange({
					category: selectedCategory,
					search: text,
					language: selectedLanguage,
					day: selectedDate,
				});
		},
		[onFilterChange, selectedCategory, selectedDate, selectedLanguage]
	);

	const [showFilterModal, hideFilterModal] = useModal(() => {
		return (
			<ModalComponent
				className={classNames("evt-modal-filters live-event-container", template)}
				title={``}
				onRequestClose={hideFilterModal}
				open={true}
				closeable
				cancellable
			>
				{renderContent()}
			</ModalComponent>
		);
	}, [isLessThan768, selectedCategory, selectedDate, selectedLanguage, template]);

	const renderSearch = (): JSX.Element => (
		<>
			{isLive && (
				<Search
					searchOnEmpty
					onSearch={handleSearchChange}
					onClear={() => handleSearchChange("")}
				/>
			)}
		</>
	);

	const renderContent = (): JSX.Element => {
		// Currently don't put logic inside NativeSelect because it's should be for all select components
		const selectViewType = isLessThan768 ? "expanded" : "default";

		return (
			<>
				{daysOptions.length > 2 ? renderDaysFilter(selectViewType) : null}
				{languageOptions.length > 2 ? renderLanguageFilter(selectViewType) : null}
				{categoryOptions.length > 2 ? renderCategoryFilter(selectViewType) : null}
			</>
		);
	};
	return (
		<>
			{isLessThan768 ? (
				<>
					{renderSearch()}
					<button onClick={showFilterModal} className="agenda-filters-button">
						<EIcon
							name="settings-sliders"
							size="medium"
						/>
					</button>
				</>
			) : (
				<>
					{renderSearch()}
					{renderContent()}
				</>
			)}
		</>
	);
};

export const filterSessions = ({
	sessions,
	filter,
}: {
	sessions: SessionPreview[];
	filter?: SessionFilterArgs;
}): SessionPreview[] => {
	let result = sessions;
	if (filter?.search) {
		result = result.filter(s => s.title.base.toUpperCase().includes(filter.search!.toUpperCase()));
	}
	if (filter?.category) {
		result = result.filter((f) => f.tracks?.includes(filter.category || ""));
	}
	if (filter?.language) {
		result = result.filter((f) => f.languages.includes(filter.language || ""));
	}
	if (filter?.day) {
		//turn timestamps into days, to get rid of the exact time factor in timestamps
		const getDay = (date: Date) => date.toLocaleString("default", { month: "long" }) + date.getDate();
		result = result.filter((f) => {
			const currentDay = getDay(new Date(f.timestamp));
			const filterDay = getDay(new Date(filter.day!));
			return currentDay === filterDay;
		});
	}
	return result;
};
